import { Content } from '@raydiant/api-client-js';
import Dialog, {
  DialogHeader,
  DialogHeading,
} from '@raydiant/raydial/components/Dialog';
import Modal from '@raydiant/raydial/components/Modal';
import Button from '@raydiant/raydial/components/Button';
import Text from '@raydiant/raydial/components/Text';
import { useToastState } from '@raydiant/raydial/components/Toast';
import useDeleteContent from '../../../../../hooks/useDeleteContent';
import { useLibrary } from '../../../../../hooks';
import { defaultLibraryPageQueryParams } from '../../../utils';
import { capitalize } from '../../../../../utilities/stringUtils';

interface DeleteLocationDialogProps {
  isOpen: boolean;
  content: Content;
  onClose: () => void;
  onSubmit: () => void;
}

const DeleteContentDialog = ({
  isOpen,
  content,
  onClose,
  onSubmit,
}: DeleteLocationDialogProps) => {
  const queryObj = {
    ...defaultLibraryPageQueryParams,
    folder_id: content.id,
    scope: '',
  };
  const isContentFolder = content.resourceType === 'folder';

  const state = useToastState();
  const deleteContent = useDeleteContent();
  const { data: libraryData, isLoading: isLibraryLoading } = useLibrary(
    queryObj,
    { enabled: isContentFolder },
  );
  const isFolderHasContent =
    isContentFolder && libraryData && libraryData?.total > 0;

  const title = isContentFolder
    ? 'Confirm delete folder'
    : 'Confirm delete content';
  const body = isContentFolder
    ? 'Are you sure you want to delete this folder? This cannot be undone.'
    : `Are you sure you want to delete the “${content.name}” ${content.resourceType}? This content may be published on devices, and this cannot be undone.`;
  const submitButtonText = isContentFolder ? 'Delete Folder' : 'Confirm';

  const handleDeleteContent = async () => {
    try {
      const title = isContentFolder ? 'Folder Deleted' : 'Content Deleted';
      const description = isContentFolder
        ? 'Folder deleted successfully'
        : `${capitalize(content.resourceType)} was deleted successfully`;

      await deleteContent.mutate({
        id: content.id,
        resourceType: content.resourceType,
      });
      state.add({
        title,
        description,
      });
      onSubmit();
    } catch (error) {
      state.add({
        title: `${capitalize(content.resourceType)} could not be deleted`,
        description: 'Please try again',
        variant: 'caution',
      });
    }
  };

  const renderUnableToDeleteDialog = () => (
    <Dialog showCloseButton={true} onClose={onClose}>
      <DialogHeader>
        <DialogHeading>
          <span className="text-black">Unable to delete folder</span>
        </DialogHeading>
      </DialogHeader>

      <div className="px-4 py-6">
        <Text className="text-black" variant="body">
          The folder you are trying to delete has content that is published.
          Please move content to other folders before proceeding.
        </Text>
      </div>

      <div className="flex p-4 pt-10">
        <Button onPress={() => onClose()} size="large" className="grow">
          Okay
        </Button>
      </div>
    </Dialog>
  );

  const renderConfirmDeleteDialog = () => (
    <Dialog showCloseButton={true} onClose={onClose}>
      <DialogHeader>
        <DialogHeading>
          <span className="text-black">{title}</span>
        </DialogHeading>
      </DialogHeader>

      <div className="px-4 py-6">
        <Text className="text-black" variant="body">
          {body}
        </Text>
      </div>

      <div className="flex flex-col-reverse justify-between pt-2 pb-4 gap-3 px-0 md:gap-6 md:flex-row md:px-4">
        <Button
          onPress={() => onClose()}
          variant="secondary"
          size="large"
          className="grow"
          isDisabled={deleteContent.isLoading}
        >
          Cancel
        </Button>
        <Button
          onPress={() => handleDeleteContent()}
          variant="caution"
          size="large"
          className="grow"
          isDisabled={deleteContent.isLoading}
          loading={deleteContent.isLoading}
        >
          {submitButtonText}
        </Button>
      </div>
    </Dialog>
  );

  if (isLibraryLoading) return null;
  return (
    <Modal isOpen={isOpen}>
      {isFolderHasContent
        ? renderUnableToDeleteDialog()
        : renderConfirmDeleteDialog()}
    </Modal>
  );
};

export default DeleteContentDialog;
